import { Component, EventEmitter, inject, OnDestroy, OnInit, Output, signal } from '@angular/core';
import { ConfigService } from 'src/app/services/config.service';
import { LocaleService } from 'src/app/services/locale.service';
import { ThemeService } from 'src/app/services/theme.service';
import { NavigationEnd, Router } from '@angular/router';
import { BroadcastService } from '../../services/broadcast.service';
import { map, Observable, Subscription } from 'rxjs';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { AccessibilityFacade } from '../../_core/accessibility/state/accessibility.facade';
import { HighContrastMode } from 'src/app/_core/accessibility/services/models/accesibility.model';
import { FooterFacade } from '../../state/footer/footer.facade';
import { FooterSocial } from '../../services/footer/models/footer.model';
import { spanishCountries } from '../../_core/utils/spanish-countries';
import { CookiesFacade } from 'src/app/_core/state/dialogs/cookies/cookies.facade';

@Component({
  selector: 'app-footer',
  templateUrl: './footer.component.html',
  styleUrls: ['./footer.component.scss'],
})
export class FooterComponent implements OnInit, OnDestroy {
  private readonly cookiesFacade = inject(CookiesFacade);

  private static readonly ODILO_SPANISH_URL = 'https://www.odilo.es/';
  private static readonly ODILO_INTERNATIONAL_URL = 'https://www.odilo.us/';

  private readonly accessibilityFacade = inject(AccessibilityFacade);
  private readonly broadcastService = inject(BroadcastService);
  private readonly configService = inject(ConfigService);
  private readonly footerFacade = inject(FooterFacade);
  private readonly dialog = inject(MatDialog);
  private readonly localeService = inject(LocaleService);
  private readonly router = inject(Router);
  private readonly themeService = inject(ThemeService);

  private routerEventsSubscription!: Subscription;
  private footerLogosSubscription!: Subscription;
  private footerLogosAltSubscription!: Subscription;

  readonly aulaMadridClientCode = 'E1155';
  readonly footerData$ = this.footerFacade.footer$;
  readonly sidebarOpen$ = this.broadcastService.sidebar$;
  readonly odiloDevelopedSrc = this.themeService.developedUrl;
  readonly opacVersion = this.configService.opacVersion;
  readonly copyrightYear = new Date().getFullYear();
  readonly showAccessibilityCookieLinks = this.configService.get().showAccessibilityCookieLinks;
  readonly highContrastMode$ = this.accessibilityFacade.highContrastMode$;
  readonly highContrastEnabled$ = this.accessibilityFacade.highContrastEnabled$;
  readonly highContrastTooltip$: Observable<string> = this.accessibilityFacade.highContrastMode$.pipe(
    map((mode) => {
      return mode === HighContrastMode.DISABLED
        ? 'footer.highContrastDisabledTooltip'
        : 'footer.highContrastEnabledTooltip';
    })
  );
  readonly showDevelopmentInfo = this.configService.get().showDevelopmentInfo;
  readonly HighContrastMode = HighContrastMode;
  readonly selectedLanguage = this.localeService.language;
  readonly languageCodes = this.configService.get().languages;
  readonly isAulaDigital = this.configService.isAulaDigital();

  footerLogos = signal<string[]>([]);
  footerLogosAlt = signal<string[]>([]);
  isLandingPage = signal<boolean>(false);
  odiloUrl = signal<string>('');
  customerVersion = signal<string>('');

  @Output()
  language = new EventEmitter<string>();

  ngOnInit() {
    this.footerFacade.getFooter(this.localeService.getLanguage()).subscribe();

    // Check for landing pages when router navigates
    // Sector 1 and sector 2 must be hidden when loaded page is landing.
    this.routerEventsSubscription = this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.isLandingPage.set(event.url.includes('landing') || event.url.includes('globalgraduates'));
      }
    });

    // FIXME This data should come in footerData$ but for now it stays in `themeService`
    this.footerLogosSubscription = this.themeService.footerLogos$.subscribe((logos) => {
      this.footerLogos.set(logos);
    });

    // FIXME This data should come in footerData$ but for now it stays in `themeService`
    this.footerLogosAltSubscription = this.themeService.footerLogosAlt$.subscribe((alt) => {
      this.footerLogosAlt.set(alt);
    });

    this.odiloUrl.set(
      spanishCountries.includes(this.configService.get().country)
        ? FooterComponent.ODILO_SPANISH_URL
        : FooterComponent.ODILO_INTERNATIONAL_URL
    );

    const customerVersion = this.configService.get().version || {
      version: 'unknown',
      build: 'unknown',
    };

    this.customerVersion.set(`${customerVersion.version} (${customerVersion.build})`);
  }

  ngOnDestroy() {
    this.routerEventsSubscription.unsubscribe();
    this.footerLogosAltSubscription.unsubscribe();
    this.footerLogosSubscription.unsubscribe();
  }

  onSocialButtonClick(network: FooterSocial): void {
    window.open(network.url, '_blank', 'noopener');
  }

  changeLanguage(lang: string) {
    this.language.emit(lang);
  }

  openCookieConfiguration(event: Event) {
    event.preventDefault();

    this.cookiesFacade.openCookiesDialog();
  }

  changeHighContrastMode(mode: HighContrastMode) {
    this.accessibilityFacade.setHighContrastMode(mode);
  }
}
